/**
 * useSecurity Hook
 *
 * TanStack Query hooks for fetching and managing security data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  SecurityScoreResponse,
  SecurityScansResponse,
  SBOMResponse,
  Vulnerability,
} from '@forgenetwork/types';

/**
 * Fetch security score for a repository
 *
 * @param repoId - The repository ID
 * @param enabled - Whether the query is enabled (default: true)
 * @returns Query result with security score data
 *
 * @example
 * ```tsx
 * function SecurityOverview({ repoId }: { repoId: string }) {
 *   const { data, isLoading } = useSecurityScore(repoId);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return <div>Score: {data?.score}</div>;
 * }
 * ```
 */
export function useSecurityScore(repoId: string, enabled = true) {
  return useQuery<SecurityScoreResponse>({
    queryKey: ['security', repoId, 'score'],
    queryFn: async () => {
      return await api.get<SecurityScoreResponse>(
        `/repositories/${repoId}/security/score`
      );
    },
    enabled: enabled && !!repoId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch all security scans for a repository
 *
 * @param repoId - The repository ID
 * @param enabled - Whether the query is enabled (default: true)
 * @returns Query result with scans list
 *
 * @example
 * ```tsx
 * function ScanHistory({ repoId }: { repoId: string }) {
 *   const { data, isLoading } = useSecurityScans(repoId);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {data?.scans.map(scan => (
 *         <ScanCard key={scan.id} scan={scan} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSecurityScans(repoId: string, enabled = true) {
  return useQuery<SecurityScansResponse>({
    queryKey: ['security', repoId, 'scans'],
    queryFn: async () => {
      return await api.get<SecurityScansResponse>(
        `/repositories/${repoId}/security/scans`
      );
    },
    enabled: enabled && !!repoId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch SBOM (Software Bill of Materials) for a repository
 *
 * @param repoId - The repository ID
 * @param enabled - Whether the query is enabled (default: true)
 * @returns Query result with SBOM data
 *
 * @example
 * ```tsx
 * function SBOMViewer({ repoId }: { repoId: string }) {
 *   const { data, isLoading } = useSBOM(repoId);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return <SBOMTable components={data?.sbom.components} />;
 * }
 * ```
 */
export function useSBOM(repoId: string, enabled = true) {
  return useQuery<SBOMResponse>({
    queryKey: ['security', repoId, 'sbom'],
    queryFn: async () => {
      return await api.get<SBOMResponse>(
        `/repositories/${repoId}/security/sbom`
      );
    },
    enabled: enabled && !!repoId,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes (SBOM changes less frequently)
  });
}

/**
 * Fetch vulnerabilities for a repository
 *
 * @param repoId - The repository ID
 * @param enabled - Whether the query is enabled (default: true)
 * @returns Query result with vulnerabilities
 *
 * @example
 * ```tsx
 * function VulnerabilityList({ repoId }: { repoId: string }) {
 *   const { data, isLoading } = useVulnerabilities(repoId);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return <VulnTable vulnerabilities={data} />;
 * }
 * ```
 */
export function useVulnerabilities(repoId: string, enabled = true) {
  return useQuery<Vulnerability[]>({
    queryKey: ['security', repoId, 'vulnerabilities'],
    queryFn: async () => {
      return await api.get<Vulnerability[]>(
        `/repositories/${repoId}/security/vulnerabilities`
      );
    },
    enabled: enabled && !!repoId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Mutation to trigger a new security scan
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * function ScanButton({ repoId }: { repoId: string }) {
 *   const triggerScan = useTriggerScan();
 *
 *   return (
 *     <button
 *       onClick={() => triggerScan.mutate(repoId)}
 *       disabled={triggerScan.isPending}
 *     >
 *       {triggerScan.isPending ? 'Scanning...' : 'Run Security Scan'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useTriggerScan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (repoId: string) => {
      return await api.post<{ scanId: string }>(
        `/repositories/${repoId}/security/scan`,
        {}
      );
    },
    onSuccess: (_data, repoId) => {
      // Invalidate all security-related caches for this repo
      queryClient.invalidateQueries({
        queryKey: ['security', repoId],
      });
      // Also invalidate repository cache as it may include security summary
      queryClient.invalidateQueries({
        queryKey: ['repository', repoId],
      });
    },
  });
}

/**
 * Get cached security score without triggering a fetch
 *
 * @param repoId - The repository ID
 * @returns Cached security score or undefined
 *
 * @example
 * ```tsx
 * function QuickSecurityBadge({ repoId }: { repoId: string }) {
 *   const score = useCachedSecurityScore(repoId);
 *
 *   return <span>{score?.grade ?? 'N/A'}</span>;
 * }
 * ```
 */
export function useCachedSecurityScore(
  repoId: string
): SecurityScoreResponse | undefined {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<SecurityScoreResponse>([
    'security',
    repoId,
    'score',
  ]);
}
